/**
 * EndTurn applier.
 *
 * Executes end-of-turn bookkeeping per §6.6:
 *
 * 1. Refill: for each empty market slot, draw 1 tile + 1 token from supply.
 *    Slot indices are preserved (no compaction).
 *    If the tile draw pile is empty for a slot that needs a tile, end the game.
 *
 * 2. Reset turn-scoped market flags:
 *    optionalWipeUsedThisTurn = false
 *    decoupleUnlockedThisTurn = false
 *    autoWipePending = false
 *
 * 3. Auto-wipe detection: after refill, if all 4 non-empty wildlife tokens are
 *    the same species → set autoWipePending = true. (P2C: flag only; P2E wires
 *    up the actual AutoWipeMarket event chain.)
 *    NOTE: Token species resolution is not possible without a per-instance
 *    species lookup. We skip auto-wipe detection for now (flag stays false)
 *    and document it as a P2E concern (requires the token species registry).
 *
 * 4. Advance seat: activeSeat = (activeSeat + 1) % playerCount.
 *    Reset phase = "marketCheck", reset actionBudget.
 *
 * 5. Solo module (§8): after refill and before advancing seat, discard one
 *    tile from the back of the draw pile and one wildlife token.
 *
 * 6. End condition (§6.7): if the tile draw pile was empty when a slot needed
 *    a tile, freeze activeSeat and set phase = "scoring".
 *
 * Spec §6.6, §6.7, §8.
 */

import {
  resolveTileDefId,
  resolveWildlifeSpecies,
} from "../data/instance-resolver";
import type { EndTurnAction } from "../types/actions";
import type { GameState } from "../types/game-state";
import type { MarketSlot } from "../types/market";
import { bumpVersion } from "./common-applier";

export const applyEndTurn = (
  state: GameState,
  _action: EndTurnAction,
): GameState => {
  const { market, supply } = state;
  let habitatDrawPile = [...supply.habitatDrawPile];
  let wildlifeBag = [...supply.wildlifeBag];
  let gameEnded = false;

  // ---------------------------------------------------------------------------
  // Step 1: Refill empty market slots (slot-only, no compaction — §6.6)
  // ---------------------------------------------------------------------------
  const newSlots = market.slots.map((slot) => {
    if (slot.tileInstanceId !== null) {
      // Slot already has a tile — no refill needed for tile side
      // (token may also already be present from a partial wipe — keep it)
      return slot;
    }

    // Slot is empty: draw tile
    if (habitatDrawPile.length === 0) {
      // §6.7: tile pile exhausted → end game
      gameEnded = true;
      return slot;
    }

    const newTileId = habitatDrawPile.shift()!;
    const newTokenId = wildlifeBag.shift() ?? null;

    return {
      ...slot,
      tileInstanceId: newTileId,
      tokenInstanceId: newTokenId,
      tileDefId: resolveTileDefId(newTileId),
      wildlifeSpecies:
        newTokenId === null ? null : resolveWildlifeSpecies(newTokenId),
    } satisfies MarketSlot;
  }) as [MarketSlot, MarketSlot, MarketSlot, MarketSlot];

  // ---------------------------------------------------------------------------
  // Step 2 (Solo §8): discard one tile and one token from supply
  // ---------------------------------------------------------------------------
  if (state.ruleset.modules.solo && !gameEnded) {
    // Drop the last tile in the draw pile (index length-1)
    if (habitatDrawPile.length > 0) {
      habitatDrawPile = habitatDrawPile.slice(0, -1);
    }
    // Drop one wildlife token (from the front of the bag — arbitrary but deterministic)
    if (wildlifeBag.length > 0) {
      wildlifeBag = wildlifeBag.slice(1);
    }
  }

  // ---------------------------------------------------------------------------
  // Step 3: Auto-wipe detection (PR-4 — wired up via MarketSlot.wildlifeSpecies).
  // §6.3: when all 4 non-empty wildlife tokens after refill are the same
  // species, set autoWipePending = true. The engine emits the flag here;
  // the AutoWipeMarket event itself is dispatched by the orchestrator.
  // ---------------------------------------------------------------------------
  const speciesPresent = newSlots
    .map((s) => s.wildlifeSpecies)
    .filter((sp): sp is NonNullable<typeof sp> => sp !== null);
  const autoWipePending =
    speciesPresent.length === 4 && new Set(speciesPresent).size === 1;

  // ---------------------------------------------------------------------------
  // Step 4: Reset turn-scoped flags and advance seat (unless game ended)
  // ---------------------------------------------------------------------------
  const playerCount = state.players.length;

  if (gameEnded) {
    return {
      ...state,
      ...bumpVersion(state),
      market: {
        ...market,
        slots: newSlots,
        autoWipePending,
        optionalWipeUsedThisTurn: false,
        decoupleUnlockedThisTurn: false,
      },
      supply: {
        ...supply,
        habitatDrawPile,
        wildlifeBag,
      },
      turn: {
        ...state.turn,
        phase: "scoring",
        // activeSeat frozen per §6.7
        actionBudget: {
          natureSpendable: 0,
          landmarkEligibleHabitats: [],
          justPlacedTileCoord: null,
        },
      },
    };
  }

  const nextSeat = (state.turn.activeSeat + 1) % playerCount;

  return {
    ...state,
    ...bumpVersion(state),
    market: {
      ...market,
      slots: newSlots,
      autoWipePending,
      optionalWipeUsedThisTurn: false,
      decoupleUnlockedThisTurn: false,
    },
    supply: {
      ...supply,
      habitatDrawPile,
      wildlifeBag,
    },
    turn: {
      activeSeat: nextSeat,
      phase: "marketCheck",
      actionBudget: {
        natureSpendable: state.players[nextSeat]?.natureTokens ?? 0,
        landmarkEligibleHabitats: [],
        justPlacedTileCoord: null,
      },
    },
  };
};
